import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { FontIcon } from 'office-ui-fabric-react/lib/Icon';
import { Text, TooltipHost } from 'office-ui-fabric-react';
import { startCase } from 'lodash';
import { useId } from '@uifabric/react-hooks';
import styles from './AdjustmentStatusIcons.style';
import { AdjustmentStatus } from '../../utils/adjustmentUtils';
import theme from '../../theme.style';
import { pxToRem } from '../../utils';

const RedExclamationIcon = { name: 'StatusCircleExclamation', color: theme.palette.accentError };

// Business requirements
// https://microsoftit.visualstudio.com/OneITVSO/_backlogs/backlog/FIN-ROY-CALC-ET-TeamByte/Features/?workitem=7374696
export default function AdjustmentStatusIcons({ status }) {
  const dotId = useId('adjustmentStatus');

  const { icon, dot } = useMemo(() => {
    switch (status) {
      case AdjustmentStatus.Draft:
        return {
          icon: null,
          dot: { text: 'Draft', color: theme.palette.accentError },
        };
      case AdjustmentStatus.ApprovalRequested:
        return {
          icon: RedExclamationIcon,
          dot: { text: 'Pending Approval', color: theme.palette.accentError },
        };
      case AdjustmentStatus.Approved:
        return {
          icon: null,
          dot: { text: 'Approved', color: theme.palette.accentTeal },
        };
      case AdjustmentStatus.Rejected:
        return {
          icon: RedExclamationIcon,
          dot: { text: 'Rejected', color: theme.palette.accentError },
        };
      case AdjustmentStatus.ReversalDraft:
        return {
          icon: null,
          dot: { text: 'Reverse Draft', color: theme.palette.accentError },
        };

      case AdjustmentStatus.ReversalApprovalRequested:
        return {
          icon: RedExclamationIcon,
          dot: { text: 'Reverse Requested', color: theme.palette.accentYellow },
        };
      case AdjustmentStatus.ReversalApproved:
        return {
          icon: null,
          dot: { text: 'Reverse Approved', color: theme.palette.accentYellow },
        };
      case AdjustmentStatus.ReversalRejected:
        return {
          icon: RedExclamationIcon,
          dot: { text: 'Reverse Rejected', color: theme.palette.accentError },
        };
      case AdjustmentStatus.AutoReversed:
        return {
          icon: {
            name: 'RevToggleKey',
            color: theme.palette.neutralDark,
            styles: { fontSize: pxToRem(10) },
          },
          dot: { text: 'Auto Reversed', color: theme.palette.accentYellow },
        };

      default:
        return { icon: null, dot: null };
    }
  }, [status]);

  return (
    <TooltipHost
      aria-label={dot?.text}
      content={dot?.text}
      calloutProps={{ target: `#${dotId}` }}
      styles={{
        root: {
          display: 'inline-flex',
          width: 'max-content',
        },
      }}
    >
      <FontIcon
        className={clsx(styles.leftIcon, !icon && styles.invisible)}
        iconName={icon?.name}
        style={{ color: icon?.color, ...icon?.styles }}
        ariaLabel="TextDocument"
      />

      <FontIcon
        iconName="LocationDot"
        ariaLabel="TextDocument"
        style={{ color: dot?.color }}
        id={dotId}
        className={clsx(styles.dotIcon)}
      />
      <Text className={styles.statusText} data-testid="status-text">
        {startCase(status)}
      </Text>
    </TooltipHost>
  );
}

AdjustmentStatusIcons.propTypes = {
  status: PropTypes.oneOf(Object.values(AdjustmentStatus)).isRequired,
};
